import axiosInstance from './axiosConfig';

const invoiceAPI = {
  getAll: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/invoices', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch invoices';
    }
  },

  getById: async (id) => {
    try {
        console.log('API: Fetching invoice with ID:', id);
        const response = await axiosInstance.get(`/invoices/${id}`);
        console.log('API: Invoice response:', response);
        return response;
    } catch (error) {
        console.error('API: Error fetching invoice:', error);
        throw error;
    }
  },

  create: async (data) => {
    try {
      const response = await axiosInstance.post('/invoices', data);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to create invoice';
    }
  },

  update: async (id, data) => {
    try {
      const response = await axiosInstance.put(`/invoices/${id}`, data);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to update invoice';
    }
  },

  delete: async (id) => {
    try {
      const response = await axiosInstance.delete(`/invoices/${id}`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to delete invoice';
    }
  },

  // Invoice specific methods
  getByCustomer: async (customerId, params = {}) => {
    try {
      const response = await axiosInstance.get(`/invoices/customer/${customerId}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch invoices by customer';
    }
  },

  getByStatus: async (status, params = {}) => {
    try {
      const response = await axiosInstance.get(`/invoices/status/${status}`, { params });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch invoices by status';
    }
  },

  getByDateRange: async (startDate, endDate, params = {}) => {
    try {
      const response = await axiosInstance.get('/invoices', {
        params: { startDate, endDate, ...params }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch invoices by date range';
    }
  },

  postToAccounting: async (id) => {
    try {
      const response = await axiosInstance.post(`/invoices/${id}/post-to-accounting`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to post invoice to accounting';
    }
  },

  send: async (id) => {
    try {
      const response = await axiosInstance.post(`/invoices/${id}/send`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to send invoice';
    }
  },

  markAsPaid: async (id, paymentData) => {
    try {
      const response = await axiosInstance.post(`/invoices/${id}/mark-as-paid`, paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to mark invoice as paid';
    }
  },

  recordPayment: async (id, paymentData) => {
    try {
        console.log('Recording payment for invoice:', id, paymentData);
        const response = await axiosInstance.post(`/invoices/${id}/mark-as-paid`, paymentData);
        return response.data;
    } catch (error) {
        console.error('API Error - Record payment:', error);
        throw error;
    }
  },  

  markAsSent: async (id) => {
    try {
      const response = await axiosInstance.post(`/invoices/${id}/mark-as-sent`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to mark invoice as sent';
    }
  },

  void: async (id) => {
    try {
      const response = await axiosInstance.post(`/invoices/${id}/void`);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to void invoice';
    }
  },

  getNextInvoiceNumber: async () => {
    try {
      const response = await axiosInstance.get('/invoices/generate-invoice-number');
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to generate invoice number';
    }
  },

  getInvoiceSummary: async (startDate, endDate) => {
    try {
      const response = await axiosInstance.get('/invoices/summary', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch invoice summary';
    }
  },

  getAgingReport: async (asOfDate) => {
    try {
      const response = await axiosInstance.get('/invoices/aging-report', {
        params: { asOfDate }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch aging report';
    }
  },

  getRevenueByCustomer: async (startDate, endDate) => {
    try {
      const response = await axiosInstance.get('/invoices/revenue-by-customer', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch revenue by customer';
    }
  },

  getRevenueByProduct: async (startDate, endDate) => {
    try {
      const response = await axiosInstance.get('/invoices/revenue-by-product', {
        params: { startDate, endDate }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to fetch revenue by product';
    }
  },

  exportToPDF: async (id) => {
    try {
      const response = await axiosInstance.get(`/invoices/${id}/export-pdf`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      // When responseType is 'blob', error body is also a Blob — parse it to read the JSON message
      if (error.response?.data instanceof Blob && error.response.data.type === 'application/json') {
        try {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          throw json.message || 'Failed to export invoice to PDF';
        } catch (parseErr) {
          if (typeof parseErr === 'string') throw parseErr;
        }
      }
      throw error.response?.data?.message || 'Failed to export invoice to PDF';
    }
  },

  sendEmail: async (id, emailData) => {
    try {
      const response = await axiosInstance.post(`/invoices/${id}/send-email`, emailData);
      return response.data;
    } catch (error) {
      throw error.response?.data?.message || 'Failed to send invoice email';
    }
  }
};

export default invoiceAPI;